import { AfterViewInit, Component, Inject, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import { AffectedPackagingDto } from 'src/app/data-transfer/entities/affected-packaging-dto';
import { AffectedPackagingTypeDto } from 'src/app/data-transfer/entities/affected-packaging-type-dto';
import { CeoDto } from 'src/app/data-transfer/entities/doc-entities/ceo-dto';
import { SigneeDto } from 'src/app/data-transfer/entities/doc-entities/signee-dto';
import { OrganizationDto } from 'src/app/data-transfer/entities/organization-dto';
import { PpwrDocumentHandlingDto } from 'src/app/data-transfer/entities/ppwr-entities/ppwr-document-handling-dto';
import { PpwrDocumentHandlingModeDto } from 'src/app/data-transfer/entities/ppwr-entities/ppwr-document-handling-mode-dto';
import { UserApiService } from 'src/app/data-transfer/services/user-api-service';
import { DialogActions } from 'src/app/model/dictionary';
import { LicenseService } from 'src/app/services/licensing-service';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { CreateDocDialogComponent, CreateDocDialogData } from '../create-doc-dialog/create-doc-dialog.component';
import {
  CreateDocSignatureDialogComponent, CreateDocSignatureDialogData
} from '../create-doc-signature-dialog/create-doc-signature-dialog.component';
import {
  CreateDocSignersDialogComponent, CreateDocSignersDialogData
} from '../create-doc-signers-dialog/create-doc-signers-dialog.component';
import { CreateDocTdDialogComponent, CreateDocTdDialogData } from '../create-doc-td-dialog/create-doc-td-dialog.component';

export interface AffectedPackagingDialogData {
  affectedPackaging: AffectedPackagingDto[];
  allowRecreate: boolean;
}

interface RecreateOptions {
  language: string;
  doc?: PpwrDocumentHandlingDto['doc'];
  signatureImage?: string;
  signatureImageContentType?: string;
  summary: string;
}

// The aggregate row replaces the detail columns with a single label and keeps the flags, so the two lists
// are derived from the same source: the label spans exactly the detail columns.
const DETAIL_COLUMNS = ['packagingType', 'name', 'articleNumber', 'brandName', 'version', 'organizationName'];
const FLAG_COLUMNS = ['recreateAllowed', 'technicalDocumentation', 'declarationOfConformity'];

@Component({
  selector: 'app-affected-packaging-dialog',
  templateUrl: './affected-packaging-dialog.component.html',
  styleUrls: ['./affected-packaging-dialog.component.scss']
})
export class AffectedPackagingDialogComponent implements AfterViewInit {

  @ViewChild(MatSort) private sort!: MatSort;

  displayedColumns = [...DETAIL_COLUMNS, ...FLAG_COLUMNS];
  readonly inaccessibleColumns = ['inaccessibleLabel', ...FLAG_COLUMNS];
  readonly inaccessibleLabelSpan = DETAIL_COLUMNS.length;
  dataSource = new MatTableDataSource<AffectedPackagingDto>();

  readonly modes = PpwrDocumentHandlingModeDto;
  readonly allowRecreate: boolean;
  readonly affectedCount: number;
  readonly hasTechnicalDocumentation: boolean;
  readonly hasDeclarationOfConformity: boolean;
  readonly hasDocuments: boolean;
  readonly showDocumentSection: boolean;
  readonly allWritable: boolean;
  readonly nonWritableNames: string;
  readonly hasForeignOrganization: boolean;
  readonly foreignOrganizationNames: string;

  recreateOptions: RecreateOptions | null = null;
  organizationLoadFailed = false;

  form: FormGroup<{ mode: FormControl<PpwrDocumentHandlingModeDto> }>;

  constructor(
    public dialogRef: MatDialogRef<AffectedPackagingDialogComponent>,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private userApiService: UserApiService,
    private translateService: TranslocoService,
    licenseService: LicenseService,
    @Inject(MAT_DIALOG_DATA) public data: AffectedPackagingDialogData
  ) {
    this.dataSource.data = data.affectedPackaging;
    this.orderEditedFirstAggregateLast();
    this.allowRecreate = data.allowRecreate;

    this.affectedCount = data.affectedPackaging
      .filter(x => !x.isEditedPackaging)
      .reduce((sum, x) => sum + (x.count ?? 1), 0);
    this.hasTechnicalDocumentation = data.affectedPackaging.some(x => x.hasTechnicalDocumentation);
    this.hasDeclarationOfConformity = data.affectedPackaging.some(x => x.hasDeclarationOfConformity);
    this.hasDocuments = this.hasTechnicalDocumentation || this.hasDeclarationOfConformity;
    this.showDocumentSection = this.hasDocuments && licenseService.isPPWRAllowed;
    this.allWritable = data.affectedPackaging.every(x => !!x.permissions?.write);
    this.nonWritableNames = this.joinWithInaccessible(
      data.affectedPackaging.filter(x => !x.permissions?.write), x => x.name);
    this.hasForeignOrganization = data.affectedPackaging.some(x => x.isForeignOrganization);
    this.foreignOrganizationNames = this.joinWithInaccessible(
      data.affectedPackaging.filter(x => x.isForeignOrganization), x => x.organizationName);

    this.form = this.fb.group({
      mode: new FormControl<PpwrDocumentHandlingModeDto>(PpwrDocumentHandlingModeDto.Delete, { nonNullable: true })
    });

    this.form.controls.mode.valueChanges.subscribe(mode => {
      if (mode === PpwrDocumentHandlingModeDto.Recreate && !this.recreateOptions) { this.openOptionsDialog(); }
    });
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  getPackagingTypeKey(packagingType: AffectedPackagingTypeDto): string {
    switch (packagingType) {
      case AffectedPackagingTypeDto.MultiMaterial: return 'affectedPackaging.types.multiMaterial';
      case AffectedPackagingTypeDto.PackagingComponent: return 'affectedPackaging.types.packagingComponent';
      case AffectedPackagingTypeDto.PackagingUnit: return 'affectedPackaging.types.packagingUnit';
      case AffectedPackagingTypeDto.PackagingSystem: return 'affectedPackaging.types.packagingSystem';
      default: return '';
    }
  }

  isAccessibleRow = (_index: number, row: AffectedPackagingDto): boolean => row.isAccessible !== false;

  isInaccessibleRow = (_index: number, row: AffectedPackagingDto): boolean => row.isAccessible === false;

  /** The same conditions as {@link canRecreate}, but for a single row of the table. */
  isRecreateAllowedFor(packaging: AffectedPackagingDto): boolean {
    return !!packaging.permissions?.write && !packaging.isForeignOrganization;
  }

  get canRecreate(): boolean {
    return this.allWritable && !this.hasForeignOrganization;
  }

  get isRecreate(): boolean {
    return this.form.controls.mode.value === PpwrDocumentHandlingModeDto.Recreate;
  }

  async openOptionsDialog(): Promise<void> {
    this.organizationLoadFailed = false;

    let options: RecreateOptions | null = null;

    try {
      options = await this.collectOptions();
    } catch (e) {
      // Nothing awaits this method, so an unhandled rejection would be silent and leave the mode claiming Recreate.
      console.error('Could not collect the document options, ', e);
    }

    if (options) {
      this.recreateOptions = options;
      return;
    }

    if (!this.recreateOptions) { this.form.controls.mode.setValue(PpwrDocumentHandlingModeDto.Delete); }
  }

  doConfirm(): void {
    if (!this.showDocumentSection || !this.allowRecreate) {
      this.dialogRef.close({ event: DialogActions.CONFIRM });
      return;
    }
    if (this.isRecreate && !this.recreateOptions) {
      this.openOptionsDialog();
      return;
    }
    this.dialogRef.close({ event: DialogActions.CONFIRM, handling: this.buildHandling() });
  }

  doReject(): void {
    this.dialogRef.close({ event: DialogActions.REJECT });
  }

  private async collectOptions(): Promise<RecreateOptions | null> {
    const languageData: CreateDocTdDialogData = {
      mode: this.hasDeclarationOfConformity ? 'doc' : 'td',
      packagingElements: []
    };
    const languageResult = await firstValueFrom(
      this.dialog.open(CreateDocTdDialogComponent, getDialogConfig(languageData, '650px')).afterClosed());
    if (!languageResult?.confirmed) { return null; }
    const language: string = languageResult.language;
    if (!this.hasDeclarationOfConformity) { return { language, summary: this.getLanguageName(language) }; }

    let organization: OrganizationDto;
    try {
      organization = await firstValueFrom(this.userApiService.getOwnOrganization(true));
    } catch (e) {
      console.error('Could not load own organization, ', e);
      this.organizationLoadFailed = true;
      return null;
    }

    const docData: CreateDocDialogData = {
      manufacturers: organization.manufacturerProfiles ?? [],
      representatives: organization.authorizedRepresentativeProfiles ?? []
    };
    const docResult = await firstValueFrom(
      this.dialog.open(CreateDocDialogComponent, getDialogConfig(docData, '600px')).afterClosed());
    if (!docResult?.confirmed) { return null; }

    const signersData: CreateDocSignersDialogData = {
      managingDirectors: organization.ceoProfiles ?? [],
      signers: organization.signeeProfiles ?? []
    };
    const signersResult = await firstValueFrom(
      this.dialog.open(CreateDocSignersDialogComponent, getDialogConfig(signersData, '600px')).afterClosed());
    if (!signersResult?.confirmed) { return null; }

    const signee = (organization.signeeProfiles ?? []).find(x => x.personProfileId === signersResult.signeeId);
    const ceo = (organization.ceoProfiles ?? []).find(x => x.personProfileId === signersResult.ceoId);
    const signingPerson = signee ?? ceo;
    if (!signingPerson) { return null; }

    const signatureData = new CreateDocSignatureDialogData(signingPerson, !signee);
    const signatureResult = await firstValueFrom(
      this.dialog.open(CreateDocSignatureDialogComponent, getDialogConfig(signatureData, '600px')).afterClosed());
    if (!signatureResult?.confirmed) { return null; }

    // A signature which was not stored on a profile travels with the save request instead.
    const signature: Blob | null = signatureResult.signatureImage ?? null;
    const signatureImage = signature ? await this.toBase64(signature) : undefined;
    const manufacturer = (organization.manufacturerProfiles ?? [])
      .find(x => x.companyProfileId === docResult.manufacturerId);

    return {
      language,
      doc: {
        manufacturerId: docResult.manufacturerId,
        authorizedRepresentativeId: docResult.authorizedRepresentativeId ?? null,
        ceoId: signersResult.ceoId,
        signeeId: signersResult.signeeId ?? null,
        language,
        addSignatureImage: !!signatureResult.addSignatureImage
      },
      signatureImage,
      signatureImageContentType: signature?.type,
      summary: [this.getLanguageName(language), manufacturer?.companyName, this.getPersonName(signingPerson)]
        .filter(part => !!part).join(' · ')
    };
  }

  /**
   * Encode a signature image, so that it can travel in the JSON save request.
   * @param blob The signature image.
   * @returns The Base64-encoded data of the signature image.
   */
  private toBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      // The reader yields a data URL, of which the backend only expects the payload.
      reader.onload = () => resolve((reader.result as string).split(',')[1] ?? '');
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  }

  private getLanguageName(language: string): string {
    return this.translateService.translate('language.' + (language === 'en' ? 'gb' : language));
  }

  private getPersonName(person: CeoDto | SigneeDto): string {
    return person.firstName + ' ' + person.lastName;
  }

  private joinWithInaccessible(
    entries: AffectedPackagingDto[],
    nameOf: (entry: AffectedPackagingDto) => string | null | undefined
  ): string {
    const names = [...new Set(
      entries.filter(x => x.isAccessible !== false).map(x => nameOf(x) ?? '').filter(name => !!name)
    )];
    const aggregate = entries.find(x => x.isAccessible === false);
    if (aggregate) {
      names.push(this.translateService.translate('affectedPackaging.documents.inaccessibleShort', { count: aggregate.count }));
    }
    return names.join(', ');
  }

  // The edited packaging and the aggregate carry no sortable data, so both stay pinned.
  private orderEditedFirstAggregateLast(): void {
    const dataSource = this.dataSource;
    const defaultSortData = dataSource.sortData;
    dataSource.sortData = (data, sort) => [
      ...data.filter(x => x.isEditedPackaging),
      ...defaultSortData.call(dataSource, data.filter(x => !x.isEditedPackaging && x.isAccessible !== false), sort),
      ...data.filter(x => !x.isEditedPackaging && x.isAccessible === false)
    ];
  }

  private buildHandling(): PpwrDocumentHandlingDto {
    const mode = this.form.controls.mode.value;
    if (mode === PpwrDocumentHandlingModeDto.Delete || !this.recreateOptions) {
      return { mode: PpwrDocumentHandlingModeDto.Delete };
    }
    const handling: PpwrDocumentHandlingDto = { mode };
    if (this.hasTechnicalDocumentation) {
      handling.technicalDocumentation = { language: this.recreateOptions.language };
    }
    if (this.hasDeclarationOfConformity) {
      handling.doc = this.recreateOptions.doc;
      if (this.recreateOptions.signatureImage) {
        handling.signatureImage = this.recreateOptions.signatureImage;
        handling.signatureImageContentType = this.recreateOptions.signatureImageContentType;
        // The signature dialog already stores the image on the profile if that is what the user asked for.
        handling.saveSignatureImage = false;
      }
    }
    return handling;
  }
}
